import React, { useEffect } from "react";
import { Activity } from "lucide-react";
import { useQzTrayConnection } from "../../../hooks/qztray/useQzTrayConnection";

/**
 * ⚡ ConnectionLatencyBox
 *
 * Muestra la latencia actual del cliente QZ Tray basada en el tiempo de respuesta del último ping o estado en vivo.
 */
const ConnectionLatencyBox: React.FC = () => {
  const { status, fetchLiveStatus, startAutoRefresh, isRefreshing } = useQzTrayConnection();

  // 🚀 Cargar datos iniciales y activar auto-refresh cada 5 s
  useEffect(() => {
    fetchLiveStatus();
    const stopAutoRefresh = startAutoRefresh(5000);
    return () => stopAutoRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 📏 Obtener latencia (valor real o 0 si no hay datos)
  const latency = status?.responseTimeMs ?? 0;

  const getLatencyColor = () => {
    if (latency < 100) return "text-green-500";
    if (latency < 200) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <div className="rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark shadow-md p-5 flex flex-col items-center justify-center transition-colors">
      {/* 🔹 Encabezado */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20">
          <Activity className="h-5 w-5 text-primary" />
        </div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Latencia
        </h4>
      </div>

      {/* 🔸 Valor de latencia */}
      <span
        className={`text-2xl font-bold ${getLatencyColor()} transition-colors`}
      >
        {latency > 0 ? `${latency} ms` : "—"}
      </span>

      {/* 🔸 Descripción */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Tiempo de respuesta promedio
      </p>

      {/* 🔸 Indicador de actualización */}
      {isRefreshing && (
        <p className="text-[11px] text-primary mt-1 animate-pulse">
          Actualizando...
        </p>
      )}
    </div>
  );
};

export default ConnectionLatencyBox;
